<script setup lang="ts">
import useUserStore from "~/stores/user";
import CategoriesAPI from "~/services/categories";
import EventsAPI from "~/services/events";
import HeaderAPI from "~/services/header";
import BillingAPI from "~/services/billing";

const route = useRoute();
const router = useRouter();
const toaster = useToast();
const color = useColorMode();
const appConfig = useRuntimeConfig().public;
const userStore = useUserStore();
const isLoggedIn = computed(() => Boolean(userStore.user));
const searchValue = computed(() => route.query.q as string);
const defaultLocale = appConfig.APP_CUSTOMER.default_language as ILocale;
const availableLocales = appConfig.APP_CUSTOMER.available_languages_list as ILocale[];
const customerName = appConfig.APP_CUSTOMER.name;
const hasThemeSwitcher = appConfig.APP_FLAGS.hasWhiteTheme;
const hasNotifications = appConfig.APP_FLAGS.hasNotifications;
const CUSTOMER_LOGO_URL = {
  white: appConfig.APP_CUSTOMER.appearance.logo_white_url,
  black: appConfig.APP_CUSTOMER.appearance.logo_black_url,
  default: appConfig.APP_CUSTOMER.appearance.logo_url,
};

const logo_url = computed(() => {
  return (color.value === "dark" ? CUSTOMER_LOGO_URL.black : CUSTOMER_LOGO_URL.white) || CUSTOMER_LOGO_URL.default || "";
});

const footer_links = computed(() => appConfig.APP_CUSTOMER.footer_links as FooterLink[]);

const footer_social_links = {
  facebookUrl: appConfig.APP_CUSTOMER.social.url_facebook,
  twitterUrl: appConfig.APP_CUSTOMER.social.url_twitter,
  instagramUrl: appConfig.APP_CUSTOMER.social.url_instagram,
  mailUrl: appConfig.APP_CUSTOMER.social.url_email,
  googlePlayUrl: appConfig.APP_CUSTOMER.social.url_google_play_store,
  appStoreUrl: appConfig.APP_CUSTOMER.social.url_app_store,
};
const { data: headerLinks, execute: loadLinks } = useAsyncData("header-links", HeaderAPI.load);

const onSearch = (searchValue: string) =>
  router.push({ path: "/search", query: { q: searchValue } });
const onLogout = () => {
  userStore.logout();
  loadLinks();

  if (route.meta.requiresAuth)
    router.push({ path: "/" });
};

onMounted(() => {
  if (isLoggedIn.value) {
    Promise.all([
      EventsAPI.selected.load(),
      CategoriesAPI.followed.load(),
      BillingAPI.loadHistory({ pageSize: 1, count: true }, { loadCommerce: true }),
    ]).catch((err) => {
      toaster.error(err?.message || err);
    });
  }
});
</script>

<template>
  <div class="homeLayout">
    <BaseHeader
      fixed
      :with-search="!$route.path.startsWith('/search')"
      with-mobile-menu
      :with-auth="!isLoggedIn"
      :with-user="userStore.user"
      :with-notifications="hasNotifications"
      :with-theme-switcher="hasThemeSwitcher"
      :links="headerLinks || []"
      :logo-image-url="logo_url"
      :search-value="searchValue"
      :default-locale="defaultLocale"
      :available-locales="availableLocales"
      @search="onSearch" @logout="onLogout()"
    />

    <div class="mt-20 md:mt-[6.5rem]" />

    <main class="homeLayout__content">
      <slot />
    </main>

    <BaseFooter
      :logo-image-url="logo_url"
      :links="footer_links"
      :social-links="footer_social_links"
      :customer-name="customerName"
      :is-theme-switcher-shown="hasThemeSwitcher"
    />
  </div>
</template>

<style lang="scss">
.homeLayout {
  background-color: var(--base-background-color);

  .homeLayout__content {
    @apply h-full;
    // TODO: implement via grid
    min-height: calc(100vh - 360px);
  }
}
</style>
